import { useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { IamRole } from '@bumble/app/__generated__/data-contracts';

interface IamUserEditProps {
  onSubmit: () => void;
  iamUserId: string;
  role: IamRole;
  name?: string;
}

export const IamUserEdit = ({
  onSubmit, iamUserId, role, name
}: IamUserEditProps) => {
  const api = useApi();

  const [newRole, setNewRole] = useState<IamRole>(role)
  const [newName, setNewName] = useState<string>(name || '')

  const editIamUser = useMutation(api.editIamUserApiV1EditIamUserPost, {
    onSuccess() {
      toast.success('Updated IAM user successfully.');
      onSubmit();
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    const trimmedName = newName.trim();
    editIamUser.mutate({
      iam_user_id: iamUserId,
      new_role: newRole,
      // An IAM user need not have a name, so an empty field means the user left
      // it alone rather than asked to clear it. Sending it empty would be
      // rejected and take the role change down with it.
      name: trimmedName || undefined,
    });
  };

  return (
    <Box pt={2}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="name"
            label="Name"
            variant="outlined"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            autoFocus
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="role-label">Role</InputLabel>
            <Select
              labelId="role-label"
              id="role"
              label="Role"
              value={newRole}
              onChange={(e) => {
                setNewRole(e.target.value as IamRole)
              }}
            >
              {Object.values(IamRole).map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="outlined" onClick={handleSubmit} disabled={!(newRole)}>
          Save
        </Button>
      </Box>
    </Box>
  );
};
